import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { cognitoConfig } from '../config/cognito';

const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId,
  ClientId: cognitoConfig.userPoolClientId,
});

// The pool's UsernameConfiguration is null (default = case-sensitive), so
// "Chelsey.krebs@..." and "chelsey.krebs@..." are different usernames at
// the Cognito layer. Normalize here so users typing their email with any
// casing land on the same Cognito user.
const normalizeUsername = (u: string): string => u.trim().toLowerCase();

export interface AuthTokens {
  accessToken: string;
  idToken: string;
}

export interface CognitoAuthUser {
  username: string;
  email?: string;
  groups?: string[];
}

function sessionToTokens(session: CognitoUserSession): AuthTokens {
  const tokens = {
    accessToken: session.getAccessToken().getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
  };
  // Signal the oa-extension's auth-relay content script so it can refresh its
  // cached JWT after silent Cognito refreshes (storage events don't fire in
  // the same tab that wrote them). Same-origin postMessage; harmless if the
  // extension isn't installed - nothing else listens for this message type.
  if (typeof window !== 'undefined') {
    window.postMessage(
      { type: 'revmac:jwt-refreshed', idToken: tokens.idToken },
      window.location.origin,
    );
  }
  return tokens;
}

function parseIdTokenPayload(idToken: string): Record<string, unknown> {
  const payload = idToken.split('.')[1];
  return JSON.parse(atob(payload));
}

export function getCurrentUser(): CognitoUser | null {
  return userPool.getCurrentUser();
}

export function getSession(): Promise<AuthTokens> {
  return new Promise((resolve, reject) => {
    const user = userPool.getCurrentUser();
    if (!user) {
      reject(new Error('No authenticated user'));
      return;
    }

    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err || new Error('No session'));
        return;
      }

      if (!session.isValid()) {
        const refreshToken = session.getRefreshToken();
        user.refreshSession(refreshToken, (refreshErr: Error | null, refreshedSession: CognitoUserSession) => {
          if (refreshErr) {
            reject(refreshErr);
            return;
          }
          resolve(sessionToTokens(refreshedSession));
        });
        return;
      }

      resolve(sessionToTokens(session));
    });
  });
}

export function signIn(username: string, password: string): Promise<AuthTokens> {
  return new Promise((resolve, reject) => {
    const normalized = normalizeUsername(username);
    const cognitoUser = new CognitoUser({
      Username: normalized,
      Pool: userPool,
    });

    cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

    const authDetails = new AuthenticationDetails({
      Username: normalized,
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        resolve(sessionToTokens(session));
      },
      onFailure: (err: Error) => {
        reject(err);
      },
      newPasswordRequired: (_userAttributes: Record<string, string>) => {
        reject(new Error('NEW_PASSWORD_REQUIRED'));
      },
    });
  });
}

export function completeNewPassword(
  username: string,
  oldPassword: string,
  newPassword: string,
): Promise<AuthTokens> {
  return new Promise((resolve, reject) => {
    const normalized = normalizeUsername(username);
    const cognitoUser = new CognitoUser({
      Username: normalized,
      Pool: userPool,
    });

    cognitoUser.setAuthenticationFlowType('USER_PASSWORD_AUTH');

    const authDetails = new AuthenticationDetails({
      Username: normalized,
      Password: oldPassword,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        resolve(sessionToTokens(session));
      },
      onFailure: (err: Error) => {
        reject(err);
      },
      newPasswordRequired: () => {
        // Pool uses UsernameAttributes=["email"], so email is already set as
        // the username. Echoing any userAttributes back here triggers
        // "Cannot modify an already provided email". Set nothing.
        cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
          onSuccess: (session: CognitoUserSession) => {
            resolve(sessionToTokens(session));
          },
          onFailure: (err: Error) => {
            reject(err);
          },
        });
      },
    });
  });
}

export function signOut(): void {
  const user = userPool.getCurrentUser();
  if (user) {
    user.signOut();
  }
}

export function getUserFromSession(tokens: AuthTokens): CognitoAuthUser {
  const payload = parseIdTokenPayload(tokens.idToken);
  return {
    username: (payload['cognito:username'] as string) || (payload['sub'] as string) || '',
    email: payload['email'] as string | undefined,
    groups: (payload['cognito:groups'] as string[]) || [],
  };
}

export function forgotPassword(username: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: normalizeUsername(username),
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
    });
  });
}

export function confirmPassword(
  username: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: normalizeUsername(username),
      Pool: userPool,
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
    });
  });
}
